// BL-177 Step 2n: which Communications layout this window gets.
//   phone   < 768 px    one screen at a time (list, conversation, People), actions collapse into a "⋯" menu
//   tablet  768-1023    two panes (list + conversation); the People panel opens over the conversation as a sheet
//   desktop >= 1024     unchanged
// Without matchMedia (tests, very old browsers) the answer is "desktop", i.e. today's layout. The CSS in styles/step2n.css uses the
// same two breakpoints, so a screen is correct even before this hook has run.
import { useEffect, useState } from 'react';

export const COMMS_PHONE_QUERY = '(max-width: 767px)';
export const COMMS_TABLET_QUERY = '(min-width: 768px) and (max-width: 1023px)';

function read() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'desktop';
    try {
        if (window.matchMedia(COMMS_PHONE_QUERY).matches) return 'phone';
        if (window.matchMedia(COMMS_TABLET_QUERY).matches) return 'tablet';
    } catch { /* fall through */ }
    return 'desktop';
}

export function commsViewportForWidth(width) {
    if (!Number.isFinite(width)) return 'desktop';
    if (width < 768) return 'phone';
    return width < 1024 ? 'tablet' : 'desktop';
}

export default function useCommsViewport() {
    const [viewport, setViewport] = useState(read);
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
        const lists = [window.matchMedia(COMMS_PHONE_QUERY), window.matchMedia(COMMS_TABLET_QUERY)].filter((list) => list && typeof list.addEventListener === 'function');
        const update = () => setViewport(read());
        update();
        lists.forEach((list) => list.addEventListener('change', update));
        return () => lists.forEach((list) => list.removeEventListener('change', update));
    }, []);
    return viewport;
}

/**
 * Keeps two CSS variables on `element` equal to the visible part of the screen (the window minus the on-screen keyboard), so a
 * full-screen phone conversation can pin its composer just above the keyboard: --ia-comms-vvh (height) and --ia-comms-vvtop (offset).
 * Only runs while `enabled`. Browsers without visualViewport keep the 100dvh fallback in the CSS.
 */
export function useVisualViewportVars(elementRef, enabled) {
    useEffect(() => {
        const node = elementRef.current;
        const viewport = typeof window !== 'undefined' ? window.visualViewport : null;
        if (!enabled || !node || !viewport) return undefined;
        const apply = () => {
            node.style.setProperty('--ia-comms-vvh', `${Math.round(viewport.height)}px`);
            node.style.setProperty('--ia-comms-vvtop', `${Math.round(viewport.offsetTop)}px`);
            // the on-screen keyboard is up when the visible part is clearly shorter than the window
            node.dataset.commsKb = window.innerHeight - viewport.height > 120 ? 'open' : 'closed';
        };
        apply();
        viewport.addEventListener('resize', apply);
        viewport.addEventListener('scroll', apply);
        return () => {
            viewport.removeEventListener('resize', apply);
            viewport.removeEventListener('scroll', apply);
            node.style.removeProperty('--ia-comms-vvh');
            node.style.removeProperty('--ia-comms-vvtop');
            delete node.dataset.commsKb;
        };
    }, [elementRef, enabled]);
}
